import { useState } from "react";
import { SkewLoader } from "react-spinners";
import { SubmitHandler, useForm } from "react-hook-form";
import Label from "../../../../components/form_fields/Label";
import OrangeButton from "../../../../components/buttons/OrangeButton";
import ModalComponent from "../../../../components/modals/ModalFormComponent";
import { useViewPort } from "../../../../helpers/useViewPort";
import { WorkTimeUnit } from "../../../../models/employee-work-time.dto";
import { useGetTimeRequestOptions } from "../../../../services/time-request-option.service";
import useUserStore from "../../../../state-management/useUserStore";
import { TIME_REQUEST_LABELS, TimeRequestKind } from "../data";
import { useTimeRequestMutations } from "../useTimeRequestApi";

type IFormInputs = {
	payPeriodId: number;
	overtimeId: number;
	numberOfHours: number;
	timeUnit: WorkTimeUnit;
	timeValue: number;
};

type TimeRequestFormModalProps = {
	kind: TimeRequestKind;
	open: boolean;
	setOpen: React.Dispatch<React.SetStateAction<boolean>>;
	// The request being edited; a new one is submitted when absent
	request?: any;
	onSaved: () => void;
};

const inputClassName = (hasError: boolean) =>
	`w-full bg-secondary-gray rounded-lg h-14 px-5 mt-2 text-sm ${
		hasError
			? "border border-red-500 focus:border-red-500 focus:outline-red-500"
			: "border-none"
	}`;

const TimeRequestFormModal = ({
	kind,
	open,
	setOpen,
	request,
	onSaved,
}: TimeRequestFormModalProps) => {
	const viewPortWidth = useViewPort();
	const companyProfile = (useUserStore().currentUser.user.companyProfiles ||
		[])[0];
	const [saving, setSaving] = useState(false);
	const isEditForm = !!request;
	const { data: options, isLoading: loadingOptions } = useGetTimeRequestOptions();
	const { create, update } = useTimeRequestMutations(kind);

	const payPeriods = [...((kind === "workTime"
		? options?.workTimePayPeriods
		: options?.overtimePayPeriods) || [])];
	const overtimes = [...(options?.overtimes || [])];
	// An edited request keeps its choices even if they are no longer offered
	if (request?.payPeriod && !payPeriods.some((p) => p.id === request.payPeriodId)) {
		payPeriods.push(request.payPeriod);
	}
	if (request?.overtime && !overtimes.some((o) => o.id === request.overtimeId)) {
		overtimes.push(request.overtime);
	}

	const {
		register,
		watch,
		handleSubmit,
		reset,
		formState: { errors },
	} = useForm<IFormInputs>({
		defaultValues: {
			payPeriodId: request?.payPeriodId,
			overtimeId: request?.overtimeId,
			numberOfHours: request?.numberOfHours,
			timeUnit: request?.timeUnit || WorkTimeUnit.HOUR,
			timeValue: request?.timeValue,
		},
	});

	const selectedOvertime = overtimes.find(
		(overtime) => overtime.id === Number(watch("overtimeId")),
	);
	const maxHours = selectedOvertime?.maxHoursPermitted || undefined;

	const handleClose = () => {
		setOpen(false);
		reset();
	};

	const onSubmit: SubmitHandler<IFormInputs> = (data) => {
		const payload = kind === "workTime"
			? {
				payPeriodId: data.payPeriodId,
				timeUnit: data.timeUnit,
				timeValue: data.timeValue,
			}
			: {
				payPeriodId: data.payPeriodId,
				overtimeId: data.overtimeId,
				numberOfHours: data.numberOfHours,
			};
		setSaving(true);
		const callbacks = {
			onSuccess: () => {
				onSaved();
				handleClose();
			},
			onSettled: () => setSaving(false),
		};
		if (isEditForm) {
			update.mutate({ id: request.id, payload }, callbacks);
		} else {
			create.mutate(
				{ payload: { ...payload, employeeId: companyProfile?.employeeId } },
				callbacks,
			);
		}
	};

	const noPayPeriods = !loadingOptions && payPeriods.length === 0;

	return (
		<ModalComponent
			width={viewPortWidth < 1024 ? "90%" : 700}
			open={open}
			title={`${isEditForm ? "Edit" : "New"} ${TIME_REQUEST_LABELS[kind].name}`}
			handleClose={handleClose}
		>
			{/* The selects show their saved values only once their options exist */}
			{loadingOptions ? (
				<div className="text-center py-10">
					<SkewLoader color="#F58B00" />
				</div>
			) : (
			<form className="mt-4" onSubmit={handleSubmit(onSubmit)}>
				<div className="space-y-5 lg:space-y-0 lg:grid grid-cols-2 gap-x-4 gap-y-6">
					<div className="w-full col-span-2">
						<Label title="Pay Period" for="payPeriodId" />
						<select
							id="payPeriodId"
							className={inputClassName(!!errors.payPeriodId || noPayPeriods)}
							{...register("payPeriodId", {
								required: "Select the pay period",
								valueAsNumber: true,
							})}
						>
							<option value="">Select Pay Period</option>
							{payPeriods.map((payPeriod) => (
								<option key={payPeriod.id} value={payPeriod.id}>
									{payPeriod.code}
									{payPeriod.status === "CLOSED" ? " (closed)" : ""}
								</option>
							))}
						</select>
						{errors.payPeriodId && (
							<p className="text-red-500 text-sm mt-1">{errors.payPeriodId.message}</p>
						)}
						{noPayPeriods && (
							<p className="text-red-500 text-sm mt-1">
								{kind === "workTime"
									? "There is no open pay period to submit work time for"
									: "There is no pay period to request overtime for"}
							</p>
						)}
						{kind === "overtime" && (
							<p className="text-gray-500 text-xs mt-1">
								Overtime approved after payroll has run for its pay period is
								paid in the next one
							</p>
						)}
					</div>

					{kind === "workTime" ? (<>
						<div className="w-full">
							<Label title="Unit" for="timeUnit" />
							<select
								id="timeUnit"
								className={inputClassName(!!errors.timeUnit)}
								{...register("timeUnit", { required: "Select the unit" })}
							>
								<option value={WorkTimeUnit.HOUR}>Hours</option>
								<option value={WorkTimeUnit.DAY}>Days</option>
							</select>
						</div>
						<div className="w-full">
							<Label title="Time Worked" for="timeValue" />
							<input
								id="timeValue"
								type="number"
								min={1}
								step={1}
								placeholder="Enter time worked"
								className={inputClassName(!!errors.timeValue)}
								{...register("timeValue", {
									required: "Enter the time worked",
									valueAsNumber: true,
									min: { value: 1, message: "Must be at least 1" },
									validate: (value) =>
										Number.isInteger(value) || "Enter a whole number",
								})}
							/>
							{errors.timeValue && (
								<p className="text-red-500 text-sm mt-1">{errors.timeValue.message}</p>
							)}
						</div>
					</>) : (<>
						<div className="w-full">
							<Label title="Overtime" for="overtimeId" />
							<select
								id="overtimeId"
								className={inputClassName(!!errors.overtimeId)}
								{...register("overtimeId", {
									required: "Select the overtime",
									valueAsNumber: true,
								})}
							>
								<option value="">Select Overtime</option>
								{overtimes.map((overtime) => (
									<option key={overtime.id} value={overtime.id}>
										{overtime.name}
									</option>
								))}
							</select>
							{errors.overtimeId && (
								<p className="text-red-500 text-sm mt-1">{errors.overtimeId.message}</p>
							)}
						</div>
						<div className="w-full">
							<Label title="Hours" for="numberOfHours" />
							<input
								id="numberOfHours"
								type="number"
								min={1}
								max={maxHours}
								step={1}
								placeholder="Enter hours"
								className={inputClassName(!!errors.numberOfHours)}
								{...register("numberOfHours", {
									required: "Enter the hours",
									valueAsNumber: true,
									min: { value: 1, message: "Must be at least 1" },
									validate: (value) => {
										if (!Number.isInteger(value)) return "Enter a whole number";
										if (maxHours && value > maxHours) {
											return `${selectedOvertime?.name} allows at most ${maxHours} hours`;
										}
										return true;
									},
								})}
							/>
							{errors.numberOfHours && (
								<p className="text-red-500 text-sm mt-1">{errors.numberOfHours.message}</p>
							)}
						</div>
					</>)}
				</div>

				<div className="flex justify-end mt-8">
					<OrangeButton
						type="submit"
						title={
							saving
								? "Saving..."
								: isEditForm ? "Save Changes" : "Submit for Approval"
						}
						className="px-8"
						disabled={saving || loadingOptions || noPayPeriods}
					/>
				</div>
			</form>
			)}
		</ModalComponent>
	);
};

export default TimeRequestFormModal;
